import React from 'react';
import { UserPointsData, PointTransaction } from '../../services/pointsAdminService';

interface UserPointsDetailsProps {
  user: UserPointsData;
  transactions: PointTransaction[];
  onAdjustPoints: () => void;
  onResetPoints: () => void;
  loading: boolean;
}

const UserPointsDetails: React.FC<UserPointsDetailsProps> = ({
  user,
  transactions,
  onAdjustPoints,
  onResetPoints,
  loading
}) => {
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionColor = (type: string): string => {
    switch (type) {
      case 'usage': return '#ef4444';
      case 'allocation': return '#10b981';
      case 'adjustment': return '#f59e0b';
      default: return '#9ca3af';
    }
  };

  const usagePercentage = user.isUnlimited 
    ? (user.dailyUsed! / user.dailyLimit!) * 100
    : (user.pointsUsed / user.monthlyPoints) * 100;

  // Check if user is on a points-enabled plan
  const isPointsUser = user.plan !== 'creator' && (
    user.plan === 'creator_lite' || 
    user.plan === 'creator_pro' || 
    user.plan === 'creator_studio' || 
    user.plan === 'creator_unlimited'
  );

  return (
    <div style={{ 
      background: 'rgba(15, 23, 42, 0.8)', 
      border: '1px solid rgba(71, 85, 105, 0.3)',
      borderRadius: '12px',
      padding: '1.5rem'
    }}>
      {/* User Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <h3 style={{ color: '#e5e7eb', fontSize: '1.1rem', margin: 0 }}>
              {user.email}
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <span style={{ 
                padding: '0.25rem 0.5rem',
                background: 'rgba(6, 182, 212, 0.2)',
                color: '#06b6d4',
                borderRadius: '4px',
                fontSize: '0.75rem'
              }}>
                {user.plan}
              </span>
              <span style={{ 
                padding: '0.25rem 0.5rem',
                background: 'rgba(139, 92, 246, 0.2)',
                color: '#8b5cf6',
                borderRadius: '4px',
                fontSize: '0.75rem'
              }}>
                {user.role}
              </span>
              {!isPointsUser && (
                <span style={{ 
                  padding: '0.25rem 0.5rem',
                  background: 'rgba(245, 158, 11, 0.2)',
                  color: '#f59e0b',
                  borderRadius: '4px',
                  fontSize: '0.75rem'
                }}>
                  Feature Count Plan
                </span>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          {isPointsUser ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={onAdjustPoints}
                disabled={loading}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Adjust Points
              </button>
              <button
                onClick={onResetPoints}
                disabled={loading}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Reset Points
              </button>
            </div>
          ) : (
            <div style={{ 
              padding: '0.5rem 1rem',
              background: 'rgba(71, 85, 105, 0.3)',
              color: '#9ca3af',
              borderRadius: '6px',
              fontSize: '0.75rem'
            }}>
              Feature-based plan
            </div>
          )}
        </div>
      </div>

      {/* Points/Features Overview */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1rem' }}>
          {isPointsUser ? 'Points Overview' : 'Plan Information'}
        </h4>
        
        {!isPointsUser ? (
          <NonPointsUserInfo user={user} />
        ) : user.isUnlimited ? (
          <UnlimitedUserStats user={user} usagePercentage={usagePercentage} />
        ) : (
          <RegularUserStats user={user} usagePercentage={usagePercentage} />
        )}
      </div>

      {/* Transactions */}
      <div>
        <h4 style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Recent Transactions ({transactions.length})
        </h4>
        
        <div style={{ 
          maxHeight: '300px', 
          overflowY: 'auto',
          border: '1px solid rgba(71, 85, 105, 0.3)',
          borderRadius: '8px'
        }}>
          {transactions.length > 0 ? (
            transactions.slice(0, 10).map((transaction) => (
              <TransactionItem 
                key={transaction.id} 
                transaction={transaction}
                getTransactionColor={getTransactionColor}
                formatDate={formatDate}
              />
            ))
          ) : (
            <div style={{ 
              padding: '2rem', 
              textAlign: 'center', 
              color: '#6b7280' 
            }}>
              {isPointsUser ? 'No transactions found' : 'Feature-based plan - no point transactions'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface NonPointsUserInfoProps {
  user: UserPointsData;
}

const NonPointsUserInfo: React.FC<NonPointsUserInfoProps> = ({ user }) => (
  <div style={{ 
    padding: '1.5rem',
    background: 'rgba(245, 158, 11, 0.1)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    borderRadius: '8px',
    textAlign: 'center'
  }}>
    <div style={{ color: '#f59e0b', fontSize: '2rem', marginBottom: '1rem' }}>
      📊
    </div>
    <h3 style={{ color: '#f59e0b', marginBottom: '0.5rem' }}>
      Feature Count Plan
    </h3>
    <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1rem' }}>
      This user is on the <strong>{user.plan}</strong> plan which uses feature counts instead of points.
    </p>
    <div style={{ 
      padding: '1rem',
      background: 'rgba(30, 41, 59, 0.5)',
      borderRadius: '6px',
      color: '#e5e7eb',
      fontSize: '0.875rem'
    }}>
      💡 To use the points system, upgrade to: Creator Lite, Creator Pro, Creator Studio, or Creator Unlimited
    </div>
    {user.plan === 'creator' && (
      <div style={{ 
        marginTop: '1rem',
        fontSize: '0.75rem',
        color: '#6b7280'
      }}>
        Free plan users have monthly feature limits (5 captions, 20 thumbnails, etc.)
      </div>
    )}
  </div>
);

interface UnlimitedUserStatsProps {
  user: UserPointsData;
  usagePercentage: number;
}

const UnlimitedUserStats: React.FC<UnlimitedUserStatsProps> = ({ user, usagePercentage }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
    <div>
      <div style={{ color: '#f59e0b', fontSize: '1.5rem', fontWeight: 'bold' }}>
        Unlimited
      </div>
      <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Plan Type</div>
    </div>
    
    <div>
      <div style={{ color: '#06b6d4', fontSize: '1.5rem', fontWeight: 'bold' }}>
        {user.dailyUsed?.toLocaleString() || 0}
      </div>
      <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
        Used Today / {user.dailyLimit?.toLocaleString() || 0}
      </div>
      
      <div style={{ 
        marginTop: '0.5rem',
        height: '4px',
        background: 'rgba(71, 85, 105, 0.3)',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${Math.min(usagePercentage, 100)}%`,
          background: usagePercentage > 80 ? '#ef4444' : '#06b6d4',
          transition: 'width 0.3s'
        }} />
      </div>
    </div>
  </div>
);

interface RegularUserStatsProps {
  user: UserPointsData;
  usagePercentage: number;
}

const RegularUserStats: React.FC<RegularUserStatsProps> = ({ user, usagePercentage }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
    <div>
      <div style={{ color: '#10b981', fontSize: '1.5rem', fontWeight: 'bold' }}>
        {user.monthlyPoints.toLocaleString()}
      </div>
      <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Monthly Allocation</div>
    </div>
    
    <div>
      <div style={{ color: '#ef4444', fontSize: '1.5rem', fontWeight: 'bold' }}>
        {user.pointsUsed.toLocaleString()}
      </div>
      <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Points Used</div>
    </div>
    
    <div>
      <div style={{ color: '#06b6d4', fontSize: '1.5rem', fontWeight: 'bold' }}>
        {user.pointsRemaining.toLocaleString()}
      </div>
      <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Remaining</div>
      
      <div style={{ 
        marginTop: '0.5rem',
        height: '4px',
        background: 'rgba(71, 85, 105, 0.3)',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${Math.min(usagePercentage, 100)}%`,
          background: usagePercentage > 80 ? '#ef4444' : '#10b981',
          transition: 'width 0.3s'
        }} />
      </div>
    </div>
  </div>
);

interface TransactionItemProps {
  transaction: PointTransaction;
  getTransactionColor: (type: string) => string;
  formatDate: (date: string) => string;
}

const TransactionItem: React.FC<TransactionItemProps> = ({ 
  transaction, 
  getTransactionColor, 
  formatDate 
}) => (
  <div style={{ 
    padding: '0.75rem',
    borderBottom: '1px solid rgba(71, 85, 105, 0.2)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}>
    <div>
      <div style={{ color: '#e5e7eb', fontSize: '0.875rem' }}>
        {transaction.description || 'Point transaction'}
      </div>
      <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
        {formatDate(transaction.createdAt)}
        {transaction.feature && ` • ${transaction.feature}`}
      </div>
    </div>
    
    <div style={{ textAlign: 'right' }}>
      <div style={{ 
        color: getTransactionColor(transaction.type),
        fontWeight: 'bold',
        fontSize: '0.875rem'
      }}>
        {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
      </div>
      <div style={{ 
        color: '#9ca3af', 
        fontSize: '0.75rem',
        textTransform: 'capitalize'
      }}>
        {transaction.type}
      </div>
    </div>
  </div>
);

export default UserPointsDetails;
